import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { formatDistance, mapsLink } from "@/lib/geolocation";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SCRIPT_ID = "google-maps-js";

function loadGoogleMaps() {
    return new Promise((resolve, reject) => {
        if (window.google?.maps) return resolve(window.google);
        if (!GOOGLE_MAPS_KEY) return reject(new Error("API key no configurada"));
        // If a previous load is in flight, wait for it
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
            existing.addEventListener("load", () => resolve(window.google));
            existing.addEventListener("error", () => reject(new Error("No se pudo cargar Google Maps")));
            return;
        }
        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&v=weekly&libraries=geometry&language=es`;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve(window.google);
        s.onerror = () => reject(new Error("No se pudo cargar Google Maps"));
        document.head.appendChild(s);
    });
}

/**
 * Dialog showing the technician's start location vs the client's address.
 * Props:
 *   open, onOpenChange
 *   techLat, techLng, accuracyM, addressResolved
 *   clientLat, clientLng, clientName, clientAddress
 *   distanceM
 *   capturedAt (ISO)
 */
export function LocationMapDialog({
    open, onOpenChange,
    techLat, techLng, accuracyM, addressResolved,
    clientLat, clientLng, clientName, clientAddress,
    distanceM, capturedAt,
}) {
    const containerRef = useRef(null);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(false);
    const distFar = distanceM != null && distanceM > 200;

    useEffect(() => {
        if (!open) return;
        if (techLat == null || techLng == null) return;
        let cancelled = false;
        setLoadError(null);
        setLoading(true);
        loadGoogleMaps()
            .then((google) => {
                if (cancelled || !containerRef.current) return;
                const center = clientLat != null && clientLng != null
                    ? { lat: (techLat + clientLat) / 2, lng: (techLng + clientLng) / 2 }
                    : { lat: techLat, lng: techLng };
                const map = new google.maps.Map(containerRef.current, {
                    center,
                    zoom: 15,
                    disableDefaultUI: false,
                    mapTypeControl: false,
                    streetViewControl: false,
                });
                // Tech marker (yellow)
                new google.maps.Marker({
                    position: { lat: techLat, lng: techLng },
                    map,
                    title: "Técnico — Inicio del servicio",
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: "#FFD62B",
                        fillOpacity: 1,
                        strokeColor: "#000",
                        strokeWeight: 2,
                        scale: 9,
                    },
                });
                // Accuracy circle
                if (accuracyM != null && accuracyM > 0) {
                    new google.maps.Circle({
                        center: { lat: techLat, lng: techLng },
                        radius: accuracyM,
                        map,
                        fillColor: "#FFD62B",
                        fillOpacity: 0.15,
                        strokeColor: "#FFD62B",
                        strokeOpacity: 0.6,
                        strokeWeight: 1,
                    });
                }
                // Client marker (red) + connecting line
                if (clientLat != null && clientLng != null) {
                    new google.maps.Marker({
                        position: { lat: clientLat, lng: clientLng },
                        map,
                        title: `Cliente — ${clientName || ""}`,
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            fillColor: "#FF0000",
                            fillOpacity: 1,
                            strokeColor: "#fff",
                            strokeWeight: 2,
                            scale: 9,
                        },
                    });
                    new google.maps.Polyline({
                        path: [
                            { lat: techLat, lng: techLng },
                            { lat: clientLat, lng: clientLng },
                        ],
                        map,
                        strokeColor: distFar ? "#FF0000" : "#000",
                        strokeOpacity: 0.7,
                        strokeWeight: 2,
                    });
                    // Fit bounds
                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend({ lat: techLat, lng: techLng });
                    bounds.extend({ lat: clientLat, lng: clientLng });
                    map.fitBounds(bounds, 40);
                }
            })
            .catch((e) => { if (!cancelled) setLoadError(e?.message || "Error al cargar el mapa"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, techLat, techLng, clientLat, clientLng, accuracyM, clientName, distFar]);

    const hasTechGps = techLat != null && techLng != null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="admin-location-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5" />
                        Ubicación de inicio del servicio
                    </DialogTitle>
                    <DialogDescription>
                        {capturedAt && `Capturada el ${new Date(capturedAt).toLocaleString("es-SV")}`}
                    </DialogDescription>
                </DialogHeader>

                {!hasTechGps ? (
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center">
                        <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm font-medium">No se registró ubicación</p>
                        <p className="text-xs text-muted-foreground mt-1">El técnico inició el servicio sin GPS disponible.</p>
                    </div>
                ) : (
                    <>
                        {/* Summary chips */}
                        <div className="flex flex-wrap items-center gap-2">
                            {distanceM != null && (
                                <Badge className={distFar ? "bg-[#FF0000] text-white hover:bg-[#FF0000]" : "bg-green-100 text-green-800 hover:bg-green-100 border-green-300"}>
                                    {distFar && <AlertTriangle className="w-3 h-3 mr-1" />}
                                    {distFar ? "Lejos" : "En el lugar"} · {formatDistance(distanceM)}
                                </Badge>
                            )}
                            {accuracyM != null && (
                                <Badge variant="outline" className="font-mono">Precisión ±{Math.round(accuracyM)}m</Badge>
                            )}
                            <a href={mapsLink(techLat, techLng)} target="_blank" rel="noreferrer" className="ml-auto" data-testid="admin-open-maps">
                                <Button size="sm" variant="outline">
                                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Abrir en Google Maps
                                </Button>
                            </a>
                        </div>

                        {/* Map container */}
                        <div className="relative rounded-lg overflow-hidden border h-[400px] bg-zinc-100">
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-zinc-100/80 z-10">
                                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                </div>
                            )}
                            {loadError ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                                    <AlertTriangle className="w-8 h-8 text-amber-500 mb-2" />
                                    <p className="text-sm font-medium">No se pudo cargar el mapa</p>
                                    <p className="text-xs text-muted-foreground mt-1 max-w-md">
                                        {loadError}. Verifica que <span className="font-mono">Maps JavaScript API</span> esté habilitada en tu proyecto de Google Cloud.
                                    </p>
                                </div>
                            ) : (
                                <div ref={containerRef} className="w-full h-full" data-testid="admin-location-map" />
                            )}
                        </div>

                        {/* Address details */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="rounded-lg border bg-[#FFD62B]/10 p-3">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFD62B] border border-black" />
                                    <span className="text-[10px] uppercase tracking-wider font-semibold">Técnico (inicio)</span>
                                </div>
                                <p className="font-mono text-xs">{techLat.toFixed(6)}, {techLng.toFixed(6)}</p>
                                {addressResolved && <p className="text-xs text-foreground/80 mt-1 leading-snug">{addressResolved}</p>}
                            </div>
                            <div className="rounded-lg border bg-[#FF0000]/5 p-3">
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF0000]" />
                                    <span className="text-[10px] uppercase tracking-wider font-semibold">Cliente</span>
                                </div>
                                {clientName && <p className="font-semibold text-xs">{clientName}</p>}
                                {clientLat != null ? (
                                    <p className="font-mono text-xs text-muted-foreground">{clientLat.toFixed(6)}, {clientLng.toFixed(6)}</p>
                                ) : (
                                    <p className="text-xs text-muted-foreground italic">Sin coordenadas geocodificadas</p>
                                )}
                                {clientAddress && <p className="text-xs text-foreground/80 mt-1 leading-snug">{clientAddress}</p>}
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
